import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Btn, Input } from "@/components/erp/ui";
import { Plus, Trash2, CheckCircle2 } from "lucide-react";

const err = (e) => toast.error(e?.response?.data?.detail || "Gagal");
const hm = (m) => `${Math.floor((m || 0) / 60)} jam ${Math.round((m || 0) % 60)} mnt`;

const Rows = ({ title, rows, set, tid }) => (
  <div>
    <div className="text-xs font-semibold mb-1">{title}</div>
    {rows.map((r, i) => (
      <div key={i} className="flex gap-2 mb-1">
        <Input className="!w-44" placeholder="nama (bebas)" data-testid={`${tid}-name-${i}`} value={r.name} onChange={(e) => set(rows.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))} />
        <Input type="number" className="!w-24" placeholder="qty" data-testid={`${tid}-qty-${i}`} value={r.qty} onChange={(e) => set(rows.map((x, j) => (j === i ? { ...x, qty: e.target.value } : x)))} />
        <button type="button" aria-label="hapus" onClick={() => set(rows.filter((_, j) => j !== i))}><Trash2 className="w-4 h-4 text-slate-400" /></button>
      </div>
    ))}
    <button type="button" className="text-xs text-sky-700 inline-flex items-center gap-1" data-testid={`${tid}-add`} onClick={() => set([...rows, { name: "", qty: "" }])}><Plus className="w-3 h-3" /> tambah</button>
  </div>
);

const Plan = ({ wid, plan, reload }) => {
  const [p, setP] = useState(plan.parts); const [v, setV] = useState(plan.variants); const [tg, setTg] = useState(plan.final_target_auto ? "" : plan.final_target || "");
  const save = async () => { try { await api.put(`/prod/wo/${wid}/retouch-plan`, { parts: p.map((x) => ({ name: x.name, qty: +x.qty })), variants: v.map((x) => ({ name: x.name, qty: +x.qty })), final_target: +tg || 0 }); toast.success("Rencana disimpan"); reload(); } catch (e) { err(e); } };
  return (
    <details className="rounded-xl border border-slate-200 p-3" data-testid="rt-plan" open={!plan.parts.length && !plan.variants.length}>
      <summary className="text-sm font-semibold cursor-pointer">Rencana part & varian (bisa dibuat tim)</summary>
      <div className="grid sm:grid-cols-2 gap-3 mt-2">
        <Rows title="Part (mis. Badan, Kepala)" rows={p} set={setP} tid="rt-part" />
        <Rows title="Final / varian rakit (mis. Figure pakai syal)" rows={v} set={setV} tid="rt-var" />
      </div>
      <label className="flex items-center gap-2 text-xs mt-2">Target total final (pcs)
        <Input type="number" className="!w-28" data-testid="rt-final-target" placeholder={String(v.reduce((a, x) => a + (+x.qty || 0), 0))} value={tg} onChange={(e) => setTg(e.target.value)} />
        <span className="text-slate-400">kosong = jumlah semua varian final</span></label>
      <Btn className="mt-2" variant="ghost" data-testid="rt-plan-save" onClick={save}>Simpan rencana</Btn>
    </details>
  );
};

const today = () => new Date(Date.now() + 7 * 3600e3).toISOString().slice(0, 10);

const FinishBar = ({ wid, d, ok, reload, onDone }) => {
  const [busy, setBusy] = useState(false);
  if (d.plan.finished_at) return <div className="rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm px-3 py-2" data-testid="rt-finished">Retouch selesai {d.plan.finished_at.slice(0, 10)}{d.plan.finished_by ? ` oleh ${d.plan.finished_by}` : ""} — sudah diteruskan ke QC 1.</div>;
  const go = async () => {
    if (!window.confirm("Selesaikan retouch WO ini dan teruskan ke QC 1? Setelah ini hasil harian tidak bisa ditambah.")) return;
    setBusy(true);
    try { await api.post(`/prod/wo/${wid}/retouch-finish`); toast.success("Retouch selesai — diteruskan ke QC 1"); reload(); onDone?.(); } catch (e) { err(e); }
    setBusy(false);
  };
  return (
    <div className="flex flex-wrap items-center gap-3">
      <Btn data-testid="rt-finish-wo" disabled={!ok || busy} onClick={go}><CheckCircle2 className="w-4 h-4" /> Finish · Kirim ke QC</Btn>
      <span className="text-xs text-slate-500" data-testid="rt-finish-hint">{ok ? "Semua part & total final sudah sesuai target." : "Tombol aktif setelah semua part & total final mencapai target."}</span>
    </div>
  );
};

const Grid = ({ wid, d, reload, onDone }) => {
  const parts = d.plan.parts; const vars = d.plan.variants; const items = [...parts, ...vars];
  const [day, setDay] = useState(today()); const [v, setV] = useState({}); const [busy, setBusy] = useState(false);
  if (!items.length) return null;
  const fin = (it) => vars.reduce((a, x) => a + ((it[x.name] || {}).ready || 0), 0);
  const tot = d.totals; const finTot = vars.reduce((a, x) => a + ((tot[x.name] || {}).ready || 0), 0); const target = d.plan.final_target;
  const ok = items.every((x) => ((tot[x.name] || {}).ready || 0) >= x.qty) && (!vars.length || finTot >= target);
  const closed = !!d.plan.finished_at; const ov = d.over_totals || {};
  const set = (n, k, x) => setV({ ...v, [n]: { ...(v[n] || {}), [k]: x } });
  const save = async () => {
    const rows = items.map((x) => ({ item: x.name, ready: +(v[x.name]?.ready || 0), defect: +(v[x.name]?.defect || 0) })).filter((r) => r.ready || r.defect);
    if (!rows.length) { toast.error("Isi jumlah dulu"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/prod/wo/${wid}/retouch-outputs`, { date: day, rows });
      const mv = Object.entries(data.moved_to_defect || {});
      toast.success(`Hasil ${day} dicatat`);
      if (mv.length) toast.warning(`Lebih dari target, sisanya dihitung cacat: ${mv.map(([k, n]) => `${k} +${n}`).join(", ")}`, { duration: 8000 });
      setV({}); reload();
    } catch (e) { err(e); }
    setBusy(false);
  };
  const days = [...d.days].reverse().filter((x) => Object.keys(x.items).length || x.minutes);
  const th = "px-2 py-1 text-left font-semibold whitespace-nowrap"; const td = "px-2 py-1 whitespace-nowrap";
  return (
    <div className="space-y-2">
    <div className="overflow-x-auto rounded-xl border border-slate-200" data-testid="rt-grid">
      <table className="text-xs w-full">
        <thead className="bg-slate-50"><tr><th className={th}>Tanggal</th>{items.map((x) => <th key={x.name} className={th}>{x.name}</th>)}{vars.length > 0 && <th className={`${th} bg-emerald-50`}>Total final/hari</th>}<th className={`${th} bg-orange-50`} title="kelebihan dari target — tetap dihitung cacat">Lebihan</th><th className={th}>Tim · jam</th></tr></thead>
        <tbody>
          {days.map((r) => (
            <tr key={r.date} className="border-t border-slate-100" data-testid={`rt-row-${r.date}`}>
              <td className={td}><b>{r.date}</b></td>
              {items.map((x) => { const c = r.items[x.name]; return <td key={x.name} className={td}>{c ? <>{c.ready}{c.defect ? <span className="text-rose-600"> /cacat {c.defect}</span> : ""}{r.over_items?.[x.name] ? <span className="text-orange-600"> (lebih {r.over_items[x.name]})</span> : ""}</> : "–"}</td>; })}
              {vars.length > 0 && <td className={`${td} bg-emerald-50 font-semibold`}>{fin(r.items)}</td>}
              <td className={`${td} bg-orange-50 text-orange-700`} data-testid={`rt-over-${r.date}`}>{r.over || "–"}</td>
              <td className={`${td} text-slate-500`}>{r.people.length} org · {hm(r.minutes)}</td>
            </tr>
          ))}
          {!closed && <tr className="border-t border-slate-200 bg-amber-50" data-testid="rt-input-row">
            <td className={td}><Input type="date" className="!w-36" data-testid="rt-in-date" max={today()} value={day} onChange={(e) => setDay(e.target.value)} /></td>
            {items.map((x, i) => (
              <td key={x.name} className={td}>
                <Input type="number" min="0" className="!w-20" placeholder="ready" data-testid={`rt-in-ready-${i}`} value={v[x.name]?.ready || ""} onChange={(e) => set(x.name, "ready", e.target.value)} />
                <Input type="number" min="0" className="!w-20 mt-1" placeholder="cacat" data-testid={`rt-in-defect-${i}`} value={v[x.name]?.defect || ""} onChange={(e) => set(x.name, "defect", e.target.value)} />
              </td>
            ))}
            {vars.length > 0 && <td className={`${td} bg-emerald-50 font-semibold`} data-testid="rt-in-final">{vars.reduce((a, x) => a + (+(v[x.name]?.ready) || 0), 0)}</td>}
            <td className={`${td} bg-orange-50 text-[10px] text-orange-700`}>otomatis</td>
            <td className={td}><Btn variant="ghost" data-testid="rt-in-save" disabled={busy} onClick={save}>Simpan</Btn></td>
          </tr>}
        </tbody>
        <tfoot className="bg-slate-900 text-white" data-testid="rt-foot">
          <tr><td className={td}><b>TOTAL</b></td>
            {items.map((x) => { const t = tot[x.name] || { ready: 0, defect: 0 }; return <td key={x.name} className={td}>{t.ready} / {x.qty}{t.defect ? ` · cacat ${t.defect}` : ""}{ov[x.name] ? ` (lebihan ${ov[x.name]})` : ""}</td>; })}
            {vars.length > 0 && <td className={`${td} font-bold`} data-testid="rt-final-total">{finTot} / {target} ({target ? Math.round(finTot * 100 / target) : 0}%)</td>}
            <td className={`${td} font-bold text-orange-300`} data-testid="rt-over-total">{Object.values(ov).reduce((a, n) => a + n, 0) || "–"}</td>
            <td className={td}>{d.people.length} org · {hm(d.minutes)}</td></tr>
        </tfoot>
      </table>
    </div>
    <FinishBar wid={wid} d={d} ok={ok} reload={reload} onDone={onDone} />
    </div>
  );
};

export const RetouchDaily = ({ wid, onDone }) => {
  const [d, setD] = useState(null);
  const load = useCallback(() => api.get(`/prod/wo/${wid}/retouch-board`).then(({ data }) => setD(data)).catch(err), [wid]);
  useEffect(() => { if (wid) load(); }, [wid, load]);
  if (!d) return null;
  const items = [...d.plan.parts, ...d.plan.variants];
  return (
    <div className="mt-4 space-y-3" data-testid="rt-daily">
      {!d.plan.finished_at && <Plan key={d.plan.updated_at || "new"} wid={wid} plan={d.plan} reload={load} />}
      <Grid wid={wid} d={d} reload={load} onDone={onDone} />
      {items.length > 0 && <div className="text-xs text-slate-500" data-testid="rt-people">Tim retouch: {d.people.join(", ") || "-"}</div>}
    </div>
  );
};
